import { z } from 'zod/v4';
// Form type
export const FormTypeSchema = z.enum([
    'proof-of-service',
    'affidavit',
    'declaration',
    'notice',
    'summons',
    'subpoena',
    'cover-sheet',
    'transmittal',
    'invoice',
    'receipt',
    'email-template',
    'letter-template',
    'other',
]);
// Court type for form applicability
export const FormCourtTypeSchema = z.enum([
    'superior',
    'federal',
    'bankruptcy',
    'appellate',
    'supreme',
    'small-claims',
    'family',
    'probate',
    'all',
]);
// Manner of service
export const MannerOfServiceSchema = z.enum([
    'personal',
    'substituted',
    'posting',
    'mailing',
    'certified-mail',
    'electronic',
    'publication',
    'diligence',
    'personally-served-agent',
]);
// Address type
export const FormAddressTypeSchema = z.enum([
    'residential',
    'business',
    'registered-agent',
    'mailing',
    'all',
]);
// Template type (rich-text vs PDF)
export const TemplateTypeSchema = z.enum([
    'rich-text',
    'pdf-form',
]);
// Form status
export const FormStatusSchema = z.enum([
    'active',
    'inactive',
    'draft',
    'archived',
]);
// Field mapping for PDF forms
export const FormFieldMappingSchema = z.object({
    id: z.string().uuid(),
    fieldName: z.string(), // PDF field name
    variableKey: z.string(), // System variable to map
    fieldType: z.enum(['text', 'checkbox', 'date', 'dropdown', 'signature']),
    required: z.boolean().default(false),
    defaultValue: z.string().optional(),
    transform: z.string().optional(), // Optional transformation function name
});
// Form schema - document templates
export const FormSchema = z.object({
    id: z.string().uuid(),
    code: z.string(), // e.g., "SC-104", "POS-PERSONAL"
    name: z.string(),
    description: z.string().optional(),
    // Classification
    formType: FormTypeSchema,
    courtType: FormCourtTypeSchema.default('all'),
    productType: z.string().optional(), // Links to product category
    // Applicability filters
    capacity: z.string().optional(), // e.g., "plaintiff", "defendant", "all"
    mannerOfService: MannerOfServiceSchema.optional(),
    state: z.string().optional(), // State-specific forms
    addressType: FormAddressTypeSchema.optional(),
    // Requirements
    notaryRequired: z.boolean().default(false),
    witnessRequired: z.boolean().default(false),
    signatureRequired: z.boolean().default(true),
    // Automation flags
    automated: z.boolean().default(false), // Auto-generated
    vendorForm: z.boolean().default(false), // Sent to vendor for completion
    // Template configuration
    templateType: TemplateTypeSchema.default('pdf-form'),
    // For rich-text templates (email templates, letters)
    templateContent: z.string().optional(), // Rich text HTML with merge variables
    availableVariables: z.array(z.string()).default([]), // Variables for this form type
    // For PDF forms
    pdfTemplateUrl: z.string().url().optional(),
    pdfFileName: z.string().optional(),
    fieldMappings: z.array(FormFieldMappingSchema).default([]),
    // Form variants (related forms)
    parentFormId: z.string().uuid().optional(), // If this is a variant
    variantName: z.string().optional(), // e.g., "Personal Serve", "Business Entity"
    // Usage stats
    usageCount: z.number().default(0),
    lastUsedAt: z.string().datetime().optional(),
    // Notes
    notes: z.string().optional(),
    instructions: z.string().optional(), // Instructions for users
    // Status
    status: FormStatusSchema.default('active'),
    // Display
    sortOrder: z.number().default(0),
    // Timestamps
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateFormSchema = FormSchema.omit({
    id: true,
    usageCount: true,
    lastUsedAt: true,
    createdAt: true,
    updatedAt: true,
});
export const UpdateFormSchema = FormSchema.partial().omit({
    id: true,
    createdAt: true,
});
// Form preview
export const FormPreviewSchema = z.object({
    id: z.string().uuid(),
    code: z.string(),
    name: z.string(),
    formType: FormTypeSchema,
    courtType: FormCourtTypeSchema,
    templateType: TemplateTypeSchema,
    automated: z.boolean(),
    status: FormStatusSchema,
    usageCount: z.number(),
});
// Template variable categories for the editor
export const TemplateVariableCategorySchema = z.object({
    category: z.string(),
    variables: z.array(z.object({
        key: z.string(), // e.g., "%OrderNumber%"
        label: z.string(), // e.g., "Order Number"
        description: z.string().optional(),
        example: z.string().optional(),
    })),
});
